import { BaseHttpClient } from "../../http/base-http-client";
import type {
  Agent,
  AgentCreationModelChoices,
  AgentModelPermissionsResponse,
  CreateAgentRequest,
  UpdateAgentRequest,
  PromoteSessionAndIngestRequestData,
  PromoteSessionAndIngestResponseData,
  ForkPreview,
  ForkRequest,
  CollaborationState,
  UpgradePreview,
  ContributionInbox,
} from "../../types/api/agent-types";
import type { PaginatedResponse } from "../../types/api/common-types";

/**
 * Agent API Client
 *
 * Handles all agent-related endpoints:
 * - Agent CRUD operations (create, list, get, update, delete)
 * - Default agent operations
 *
 * Follows SRP: Single responsibility for agent operations
 * Follows SOC: Separated from business logic and UI concerns
 */
export class AgentApiClient extends BaseHttpClient {
  /**
   * Create a new agent
   * @param data - Agent creation data including name, description, and optional settings
   * @returns Promise<Agent> - Created agent information
   */
  async createAgent(data: CreateAgentRequest): Promise<Agent> {
    return this.post<Agent>("agents", data);
  }

  /**
   * The chat models the caller may start a NEW agent on: the organisation
   * default plus any alternatives the caller may delegate. UX assistance
   * only — creation re-authorizes server-side.
   */
  async getCreationModelChoices(): Promise<AgentCreationModelChoices> {
    return this.get<AgentCreationModelChoices>("agents/creation-model-choices");
  }

  /** Backend-authoritative Agent model permissions projection. */
  async getModelPermissions(
    agentId: string
  ): Promise<AgentModelPermissionsResponse> {
    return this.get<AgentModelPermissionsResponse>(
      `agents/${agentId}/model-permissions`
    );
  }

  /**
   * Explicitly enable/disable one model permission for the Agent — the ONLY
   * ordinary UX path that mutates Agent model authority. Returns the fresh
   * authoritative projection.
   */
  async setModelPermission(
    agentId: string,
    modelId: string,
    enabled: boolean
  ): Promise<AgentModelPermissionsResponse> {
    return this.put<AgentModelPermissionsResponse>(
      `agents/${agentId}/model-permissions/${modelId}`,
      { enabled }
    );
  }

  /**
   * List user's agents with optional pagination
   * @param params - Optional pagination parameters (skip, limit)
   * @returns Promise<PaginatedResponse<Agent>> - Paginated list of user's agents
   */
  async listAgents(params?: {
    skip?: number;
    limit?: number;
    /** `chat` lists only agents the user can converse with (L7). */
    access?: "any" | "chat";
  }): Promise<PaginatedResponse<Agent>> {
    // Only send parameters the backend expects
    const queryParams: { skip?: number; limit?: number; access?: string } = {};
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.access !== undefined) queryParams.access = params.access;

    return this.get<PaginatedResponse<Agent>>("agents", queryParams);
  }

  /**
   * Get agent details
   * @param agentId - Agent ID to retrieve
   * @returns Promise<Agent> - Agent details
   */
  async getAgent(agentId: string): Promise<Agent> {
    return this.get<Agent>(`agents/${agentId}`);
  }

  /**
   * Get user's default agent
   * @returns Promise<Agent> - User's default agent information
   */
  async getDefaultAgent(): Promise<Agent> {
    return this.get<Agent>("agents/default-agent");
  }

  /**
   * Update agent properties
   * @param agentId - Agent ID to update
   * @param data - Agent update data (name, description, preprompt, scope, organization)
   * @returns Promise<Agent> - Updated agent information
   */
  async updateAgent(agentId: string, data: UpdateAgentRequest): Promise<Agent> {
    return this.put<Agent>(`agents/${agentId}`, data);
  }

  /**
   * Delete an agent
   * @param agentId - Agent ID to delete
   * @returns Promise<void> - Confirmation of deletion
   */
  async deleteAgent(agentId: string): Promise<void> {
    return this.delete<void>(`agents/${agentId}`);
  }

  /**
   * Upload (replace) an agent's custom avatar image.
   * @param agentId - Agent ID
   * @param file - PNG or JPEG image (<= 2 MB); validated server-side
   * @returns Promise<Agent> - Updated agent (with a fresh avatar_url)
   */
  async uploadAgentAvatar(agentId: string, file: File): Promise<Agent> {
    const formData = new FormData();
    formData.append("file", file);
    return this.put<Agent>(`agents/${agentId}/avatar`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  /**
   * Remove an agent's custom avatar (reverts to the default mascot).
   * @param agentId - Agent ID
   * @returns Promise<Agent> - Updated agent (avatar_url cleared)
   */
  async deleteAgentAvatar(agentId: string): Promise<Agent> {
    return this.delete<Agent>(`agents/${agentId}/avatar`);
  }

  /**
   * What forking this agent would carry: the source's tools + resource slots
   * with the resources the caller can map them to. Read-only; persists nothing.
   */
  async getForkPreview(agentId: string): Promise<ForkPreview> {
    return this.get<ForkPreview>(`agents/${agentId}/fork-preview`);
  }

  /**
   * Fork this agent into a fresh OWNED copy (design + workflows crossed,
   * authority re-rooted under the caller). Returns the new agent.
   */
  async forkAgent(agentId: string, body: ForkRequest): Promise<Agent> {
    return this.post<Agent>(`agents/${agentId}/fork`, body);
  }

  /** The outgoing collaboration button's state (propose / submitted / …). */
  async getCollaborationState(agentId: string): Promise<CollaborationState> {
    return this.get<CollaborationState>(
      `agents/${agentId}/collaboration-state`
    );
  }

  /** Share changes: idempotent freeze → assess → submit (Backend truth). */
  async contributeUpstream(agentId: string): Promise<CollaborationState> {
    return this.post<CollaborationState>(`agents/${agentId}/contribute`, {});
  }

  async getUpgradePreview(agentId: string): Promise<UpgradePreview> {
    return this.get<UpgradePreview>(`agents/${agentId}/upgrade-preview`);
  }

  async applyUpgrade(
    agentId: string,
    resourceMappings: Record<string, string> = {}
  ): Promise<CollaborationState> {
    return this.post<CollaborationState>(`agents/${agentId}/upgrade`, {
      resource_mappings: resourceMappings,
    });
  }

  /** Continue independently: sever the upstream lineage in place. */
  async detachAgent(agentId: string): Promise<CollaborationState> {
    return this.post<CollaborationState>(`agents/${agentId}/detach`, {});
  }

  /** Publish Agent: submit this root for organisation publication. */
  async publishAgent(agentId: string): Promise<CollaborationState> {
    return this.post<CollaborationState>(`agents/${agentId}/publication`, {});
  }

  /** Contributions submitted one hop up TO this agent (owner-only inbox). */
  async listContributions(agentId: string): Promise<ContributionInbox> {
    return this.get<ContributionInbox>(`agents/${agentId}/contributions`);
  }

  /** Apply a submitted contribution in place; returns the refreshed inbox. */
  async acceptContribution(
    agentId: string,
    candidateId: string
  ): Promise<ContributionInbox> {
    return this.post<ContributionInbox>(
      `agents/${agentId}/contributions/${candidateId}/accept`,
      {}
    );
  }

  /** Reject a submitted contribution (204). */
  async rejectContribution(
    agentId: string,
    candidateId: string
  ): Promise<void> {
    return this.post<void>(
      `agents/${agentId}/contributions/${candidateId}/reject`,
      {}
    );
  }

  /**
   * Promote a session and ingest content
   * Creates a new private agent from a default agent session and initiates ingestion
   * @param data - Promotion request data including session ID, ingestion type, and data
   * @returns Promise<PromoteSessionAndIngestResponseData> - New agent ID and optional presigned data
   */
  async promoteSessionAndIngest(
    data: PromoteSessionAndIngestRequestData
  ): Promise<PromoteSessionAndIngestResponseData> {
    return this.post<PromoteSessionAndIngestResponseData>(
      "agents/promote-session-and-ingest",
      data
    );
  }
}

/**
 * Create a default AgentApiClient instance
 * @returns AgentApiClient - Configured agent API client
 */
export function createAgentApiClient(): AgentApiClient {
  return new AgentApiClient();
}

/**
 * Default AgentApiClient instance for immediate use
 */
export const agentApiClient = createAgentApiClient();
